import React, { useContext, useEffect, useState } from 'react';

import { AuthContext } from '../../context/AuthContext';
import { ZeosApiContext } from '../../context/ZeosApiContext';
import { createLogger } from '../../services/Logger';
import {
    buildCompanyCsvTemplate, IBulkCompanyRow, validateCompanyCsv
} from '../../services/helpers/CompanyCsvHelpers';
import { mapCompanyFormToContract } from '../../services/helpers/CompanyHelpers';
import {
    buildPropertyCsvTemplate, ICompanyKeyContext, IPropertyCsvLookups, validatePropertyCsv
} from '../../services/helpers/PropertyCsvHelpers';
import { mapPropertyFormToContract } from '../../services/helpers/PropertyHelpers';
import { contentWrapperClass } from '../../services/helpers/ThemeHelpers';
import { ZeosListEnum, ZeosListOptionEnum } from '../../services/zeos-api/enums/ListUtility';
import { zeosDialog } from './confirm-dialog/ConfirmDialog';
import { ZeosButton } from './ZeosButton';
import { ZeosFileUpload } from './ZeosFileUpload';
import { ZeosLabel } from './ZeosLabel';
import { ZeosModal } from './ZeosModal';
import { ZeosModalSpinner } from './ZeosModalSpinner';
import { ZeosSpinner } from './ZeosSpinner';

const log = createLogger("BulkPropertyUpload");

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  companyName?: string;
  onCreated: () => void | Promise<void>;
}

interface IUploadFormData {
  file: File[];
}

const emptyUploadForm: IUploadFormData = { file: [] };

const downloadCsv = (filename: string, content: string) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Reusable "Add Property in Bulk" modal: download a CSV template built from
 * the live HVAC/Prior Work/Building Type reference lists, upload a filled-in
 * CSV, validate every row against the same rules the single Property form
 * uses, then create all valid rows via the existing Property create endpoint.
 *
 * Optionally also bulk-creates the companies those properties belong to: an
 * optional companies CSV, joined to property rows via a companyKey column
 * (blank companyKey = the companyId this component was opened with).
 *
 * Self-contained (owns its own ZeosModal) so it can be dropped into any
 * screen with just isOpen/onClose/companyId/onCreated.
 */
export const BulkPropertyUpload = (props: IProps) => {
  const { isOpen, onClose, companyId, companyName, onCreated } = props;
  const api = useContext(ZeosApiContext);
  const auth = useContext(AuthContext);

  const [lookups, setLookups] = useState<IPropertyCsvLookups | null>(null);
  const [isLoadingLookups, setIsLoadingLookups] = useState(false);
  const [uploadForm, setUploadForm] = useState<IUploadFormData>(emptyUploadForm);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [rowResults, setRowResults] = useState<
    { line: number; name: string; errors: string[] }[]
  >([]);
  const [validRowCount, setValidRowCount] = useState(0);
  const [pendingRows, setPendingRows] = useState<
    ReturnType<typeof validatePropertyCsv>["rows"]
  >([]);

  const [companyUploadForm, setCompanyUploadForm] = useState<IUploadFormData>(emptyUploadForm);
  const [companyFileErrors, setCompanyFileErrors] = useState<string[]>([]);
  const [companyRowResults, setCompanyRowResults] = useState<
    { line: number; key: string; errors: string[] }[]
  >([]);
  const [validCompanyRowCount, setValidCompanyRowCount] = useState(0);
  const [pendingCompanyRows, setPendingCompanyRows] = useState<IBulkCompanyRow[]>([]);
  // Companies already created for this open/file, keyed by companyKey. Property/create-batch
  // is all-or-nothing, but a retry after it fails must not re-run the company batch too —
  // that has no undo. Populated once and reused across retries until the file changes.
  const [createdCompanyIds, setCreatedCompanyIds] = useState<Map<string, number>>(new Map());

  const [isCreating, setIsCreating] = useState(false);
  const [creatingMessage, setCreatingMessage] = useState("Creating...");

  useEffect(() => {
    if (!isOpen) {
      setUploadForm(emptyUploadForm);
      setFileErrors([]);
      setRowResults([]);
      setValidRowCount(0);
      setPendingRows([]);
      setCompanyUploadForm(emptyUploadForm);
      setCompanyFileErrors([]);
      setCompanyRowResults([]);
      setValidCompanyRowCount(0);
      setPendingCompanyRows([]);
      setCreatedCompanyIds(new Map());
      return;
    }

    setLookups(null);
    const loadLookups = async () => {
      setIsLoadingLookups(true);
      try {
        const [hvac, priorWork, buildingType] = await Promise.all([
          api.ListUtility.getListAsync(
            ZeosListEnum.HvacPrimaryType,
            ZeosListOptionEnum.excludeUtility
          ),
          api.ListUtility.getListAsync(ZeosListEnum.BuildingPriorWorkType),
          api.ListUtility.getListAsync(ZeosListEnum.BuildingType),
        ]);
        setLookups({
          hvacOptions: hvac.data ?? [],
          priorWorkOptions: priorWork.data ?? [],
          buildingTypeOptions: buildingType.data ?? [],
        });
      } catch (error) {
        log.error("Failed to load reference lists for bulk upload", { error });
        zeosDialog("Failed to load reference data. Please try again.");
      } finally {
        setIsLoadingLookups(false);
      }
    };

    void loadLookups();
  }, [isOpen, api.ListUtility]);

  const handleDownloadTemplate = () => {
    if (!lookups) {
      return;
    }
    downloadCsv("property-bulk-template.csv", buildPropertyCsvTemplate(lookups));
  };

  const handleDownloadCompanyTemplate = () => {
    downloadCsv("company-bulk-template.csv", buildCompanyCsvTemplate());
  };

  // Parses the optional companies CSV whenever it changes. Runs independently
  // of the properties parse below, which depends on this one's results to
  // resolve each property row's companyKey.
  useEffect(() => {
    const file = companyUploadForm.file[0];
    setCompanyFileErrors([]);
    setCompanyRowResults([]);
    setValidCompanyRowCount(0);
    setPendingCompanyRows([]);
    setCreatedCompanyIds(new Map());

    if (!file) {
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const text = await file.text();
        if (cancelled) {
          return;
        }
        const result = validateCompanyCsv(text);
        if (result.fileErrors.length > 0) {
          setCompanyFileErrors(result.fileErrors);
          return;
        }
        setCompanyRowResults(
          result.rows.map((row) => ({
            line: row.line,
            key: row.companyKey,
            errors: row.errors,
          }))
        );
        setValidCompanyRowCount(result.rows.filter((row) => row.errors.length === 0).length);
        setPendingCompanyRows(result.rows);
      } catch (error) {
        log.error("Failed to parse bulk upload companies CSV", { error });
        setCompanyFileErrors([
          "Could not read the companies CSV file. Please check the format and try again.",
        ]);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [companyUploadForm.file]);

  // ZeosFileUpload writes into formData.file via the standard useState functional
  // setter (see PropertyForm's usage) — parsing runs here as a reaction to that,
  // not inline in the setter itself. Also re-runs when the companies CSV changes,
  // since that's what a non-blank companyKey resolves against.
  useEffect(() => {
    const file = uploadForm.file[0];
    setFileErrors([]);
    setRowResults([]);
    setValidRowCount(0);
    setPendingRows([]);

    if (!file || !lookups) {
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const text = await file.text();
        if (cancelled) {
          return;
        }
        const companyKeyContext: ICompanyKeyContext = {
          companyFileUploaded: companyUploadForm.file.length > 0,
          validKeys: new Set(pendingCompanyRows.map((row) => row.companyKey.toLowerCase())),
        };
        const result = validatePropertyCsv(text, lookups, companyKeyContext);
        if (result.fileErrors.length > 0) {
          setFileErrors(result.fileErrors);
          return;
        }
        setRowResults(
          result.rows.map((row) => ({
            line: row.line,
            name: row.formData.propertyName || "(no name)",
            errors: row.errors,
          }))
        );
        setValidRowCount(result.rows.filter((row) => row.errors.length === 0).length);
        setPendingRows(result.rows);
      } catch (error) {
        log.error("Failed to parse bulk upload CSV", { error });
        setFileErrors(["Could not read the CSV file. Please check the format and try again."]);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [uploadForm.file, lookups, companyUploadForm.file, pendingCompanyRows]);

  const hasBlockingErrors =
    fileErrors.length > 0 ||
    rowResults.some((row) => row.errors.length > 0) ||
    companyFileErrors.length > 0 ||
    companyRowResults.some((row) => row.errors.length > 0);
  const canCreate = pendingRows.length > 0 && !hasBlockingErrors && !isCreating;

  const handleCreate = async () => {
    if (!canCreate) {
      return;
    }
    setIsCreating(true);
    setFileErrors([]);
    const companyKeyToId = new Map(createdCompanyIds);

    try {
      if (pendingCompanyRows.length > 0 && createdCompanyIds.size === 0) {
        setCreatingMessage("Creating companies...");
        const zeosUser = await auth.zeosUserAsync();
        const customerId = zeosUser?.customerClaims?.[0]?.customerId;
        if (!customerId) {
          setFileErrors(["Could not resolve your customer account — nothing was created."]);
          return;
        }

        try {
          const contracts = pendingCompanyRows.map((row) =>
            mapCompanyFormToContract(row.formData, customerId)
          );
          const result = await api.Company.postCompaniesBatchAsync(contracts);
          const created = result.data;
          if (!created || created.length !== pendingCompanyRows.length) {
            throw new Error(result.message || "Unknown error");
          }
          // Safe to zip by index: CompanyData.AddManyAsync returns the same
          // list it was given (AddRange + SaveChangesAsync populates each
          // entity's identity in place), so response order == request order.
          pendingCompanyRows.forEach((row, idx) => {
            const companyIdCreated = created[idx]?.companyId;
            if (companyIdCreated) {
              companyKeyToId.set(row.companyKey.toLowerCase(), companyIdCreated);
            }
          });
          // Remember these: property/create-batch is all-or-nothing, but if it
          // fails a retry must not re-run this batch and duplicate the companies.
          setCreatedCompanyIds(new Map(companyKeyToId));
          // Refresh claims so the new companies show up in the company
          // switcher without a manual logout (it reads from cached claims).
          await auth.zeosUserAsync(true);
        } catch (error) {
          log.error("Failed to create companies from bulk upload", { error });
          setFileErrors(["Failed to create companies — no properties were created either."]);
          return;
        }
      }

      setCreatingMessage(`Creating ${pendingRows.length} properties...`);
      try {
        const contracts = pendingRows.map((row) => {
          const targetCompanyId = row.companyKey
            ? companyKeyToId.get(row.companyKey.toLowerCase())
            : companyId;
          if (!targetCompanyId) {
            throw new Error(`Could not resolve target company for row ${row.line}`);
          }
          return mapPropertyFormToContract(row.formData, targetCompanyId);
        });

        const result = await api.Property.postPropertiesBatchAsync(contracts);
        if (!result.data || result.data.length !== contracts.length) {
          throw new Error(result.message || "Unknown error");
        }
      } catch (error) {
        log.error("Failed to create properties from bulk upload", { error });
        setFileErrors(["No properties were created — please try again."]);
        return;
      }

      await onCreated();
      onClose();
    } finally {
      setIsCreating(false);
    }
  };

  const currentCompanyLabel = companyName ?? "the currently selected company";

  const referenceHints = lookups
    ? [
        'land: blank, "Owned", or "Leased"',
        'hasWifi: blank, "Yes", or "No"',
        'paysBill: blank, "Owner", "Occupant", or "Tenant-Resident"',
        `buildingType: blank or one of ${lookups.buildingTypeOptions
          .map((o) => o.display)
          .join(", ")}`,
        `companyKey: blank to add to ${currentCompanyLabel}, or a key from the companies csv below`,
      ]
    : [];

  return (
    <ZeosModal isOpen={isOpen} onClose={onClose}>
      <div className={`${contentWrapperClass("form")} max-h-[80vh] overflow-y-auto`}>
        <h3 className="text-xl font-semibold text-zeos-black mb-4">
          Add Property in Bulk
        </h3>

        {isLoadingLookups ? (
          <ZeosSpinner message="Loading reference data..." />
        ) : (
          <>
            <div className="mb-6">
              <ZeosLabel label="1. (Optional) Add multiple companies" />
              <p className="text-xs text-gray-500 mb-2">
                Leave this out to add every property to {currentCompanyLabel}. To
                also bulk-create new companies, download this template, fill in
                a <code>companyKey</code> per company, and reference it from the
                properties csv below.
              </p>
              <ZeosButton
                label="Download companies csv template"
                type="button"
                mode="action"
                onPress={handleDownloadCompanyTemplate}
              />
              <div className="mt-2">
                <ZeosFileUpload
                  formData={companyUploadForm}
                  setFormData={setCompanyUploadForm}
                  errors={{}}
                  label="Choose companies CSV file"
                  accept=".csv,text/csv"
                />
              </div>
              {companyFileErrors.length > 0 && (
                <div className="mt-2 text-sm text-zeos-orange-dk">
                  {companyFileErrors.map((error) => (
                    <p key={error}>{error}</p>
                  ))}
                </div>
              )}
              {companyRowResults.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-zeos-black mb-1">
                    {validCompanyRowCount} of {companyRowResults.length} companies
                    ready to create.
                  </p>
                  {companyRowResults.some((row) => row.errors.length > 0) && (
                    <ul className="text-xs text-zeos-orange-dk space-y-1 max-h-32 overflow-y-auto">
                      {companyRowResults
                        .filter((row) => row.errors.length > 0)
                        .map((row) => (
                          <li key={row.line}>
                            Row {row.line} ({row.key || "no key"}): {row.errors.join("; ")}
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="mb-6">
              <ZeosLabel label="2. Download csv template" />
              <ZeosButton
                label="Download csv template"
                type="button"
                mode="action"
                onPress={handleDownloadTemplate}
                isDisabled={!lookups}
              />
              {referenceHints.length > 0 && (
                <ul className="text-xs text-gray-500 mt-2 list-disc list-inside">
                  {referenceHints.map((hint) => (
                    <li key={hint}>{hint}</li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Property images and utility linking aren't part of the template
                — add those per-property after import.
              </p>
            </div>

            <div className="mb-4">
              <ZeosLabel label="3. Upload filled-in csv" />
              <ZeosFileUpload
                formData={uploadForm}
                setFormData={setUploadForm}
                errors={{}}
                label="Choose CSV file"
                accept=".csv,text/csv"
              />
            </div>

            {fileErrors.length > 0 && (
              <div className="mb-4 text-sm text-zeos-orange-dk">
                {fileErrors.map((error) => (
                  <p key={error}>{error}</p>
                ))}
              </div>
            )}

            {rowResults.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-zeos-black mb-2">
                  {validRowCount} of {rowResults.length} properties ready to create.
                </p>
                {rowResults.some((row) => row.errors.length > 0) && (
                  <ul className="text-xs text-zeos-orange-dk space-y-1 max-h-40 overflow-y-auto">
                    {rowResults
                      .filter((row) => row.errors.length > 0)
                      .map((row) => (
                        <li key={row.line}>
                          Row {row.line} ({row.name}): {row.errors.join("; ")}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="mt-4 flex gap-2 justify-end">
        <ZeosButton
          label={isCreating ? "Creating..." : "Create"}
          type="button"
          mode="action"
          onPress={handleCreate}
          isDisabled={!canCreate}
        />
        <ZeosButton label="Cancel" type="button" mode="prev" onPress={onClose} />
      </div>
      <ZeosModalSpinner isVisible={isCreating} message={creatingMessage} />
    </ZeosModal>
  );
};
